import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Upload, Play, Pause, RefreshCw, Download, Share2,
  ChevronRight, ChevronLeft, Check, Loader2, Wand2,
  Sparkles, Volume2, VolumeX, X, Baby, Music, Zap,
  Star, TrendingUp, Camera, Video, Clock, User, Dog,
  Swords, Film
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { ugcAPI, videosAPI } from "../lib/api";
import { UpgradeModal } from "../components/UpgradeModal";
import MediaPickerModal, { MediaPickerButton } from "../components/MediaPickerModal";

// ===== VIRAL TEMPLATES =====
const VIRAL_TEMPLATES = [
  {
    id: "transformation",
    label: "Transformation",
    description: "Transform anything into anything with action sequence",
    icon: "🔮",
    color: "violet",
    trending: true,
    views: "NEW",
    isNew: true,
    isHot: true
  },
  {
    id: "dancing_baby",
    label: "Dancing Baby",
    description: "Transform into an adorable dancing toddler",
    icon: "👶",
    color: "pink",
    trending: true,
    views: "2.5B+"
  },
  {
    id: "singing_baby",
    label: "Singing Baby",
    description: "Baby sings along to your favorite song",
    icon: "🎤",
    color: "rose",
    trending: true,
    views: "3.2B+",
    isNew: true
  },
  {
    id: "future_me",
    label: "Future Me",
    description: "See yourself at 70-80 years old",
    icon: "👴",
    color: "amber",
    trending: true,
    views: "1.8B+"
  },
  {
    id: "anime_me",
    label: "Anime Me",
    description: "Transform into anime or cartoon style",
    icon: "🎌",
    color: "purple",
    trending: true,
    views: "2.1B+"
  },
  {
    id: "pet_twin",
    label: "Pet Twin",
    description: "Create a pet that looks like you",
    icon: "🐕",
    color: "emerald",
    trending: false,
    views: "890M+"
  },
  {
    id: "time_traveler",
    label: "Time Traveler",
    description: "Visit different decades in style",
    icon: "⏰",
    color: "cyan",
    trending: false,
    views: "750M+"
  },
  {
    id: "action_hero",
    label: "Action Hero",
    description: "Become a superhero or sports star",
    icon: "🦸",
    color: "red",
    trending: false,
    views: "1.2B+"
  },
];

// ===== DANCING BABY OPTIONS =====
const BABY_DANCE_STYLES = [
  { id: "cute_bounce", label: "Cute Bounce", description: "Adorable bouncy movements", icon: "💃" },
  { id: "hip_hop", label: "Hip Hop", description: "Trendy hip hop moves", icon: "🎤" },
  { id: "disco", label: "Disco Fever", description: "Groovy disco vibes", icon: "🕺" },
  { id: "ballet", label: "Ballet Twirl", description: "Elegant ballet movements", icon: "🩰" },
];

const BABY_COSTUMES = [
  { id: "classic_baby", label: "Classic Baby", description: "Cute colorful onesie", icon: "👶" },
  { id: "hip_hop", label: "Hip Hop Star", description: "Cool cap & chains", icon: "🎤" },
  { id: "superhero", label: "Superhero", description: "Cape & mask", icon: "🦸" },
  { id: "police", label: "Police Officer", description: "Tiny uniform & badge", icon: "👮" },
  { id: "princess", label: "Princess/Prince", description: "Tiara & sparkles", icon: "👑" },
  { id: "doctor", label: "Doctor", description: "White coat & stethoscope", icon: "🩺" },
  { id: "astronaut", label: "Astronaut", description: "Spacesuit & helmet", icon: "🚀" },
  { id: "chef", label: "Chef", description: "Chef hat & apron", icon: "👨‍🍳" },
];

const BABY_SETTINGS = [
  { id: "colorful", label: "Colorful Party", description: "Balloons & confetti", icon: "🎈" },
  { id: "living_room", label: "Living Room", description: "Cozy home vibe", icon: "🏠" },
  { id: "dance_studio", label: "Dance Studio", description: "Mirrors & spotlights", icon: "💃" },
  { id: "disco", label: "Disco Club", description: "Disco ball & neon", icon: "🪩" },
  { id: "outdoor_park", label: "Outdoor Park", description: "Sunny & green", icon: "🌳" },
  { id: "stage", label: "Concert Stage", description: "Spotlights & fog", icon: "🎤" },
  { id: "bedroom", label: "Kids Bedroom", description: "Toys & fun decor", icon: "🧸" },
];

// ===== SINGING BABY OPTIONS =====
const SINGING_STYLES = [
  { id: "cute_sing", label: "Cute Singing", description: "Adorable lip-sync performance", icon: "🎤" },
  { id: "karaoke", label: "Karaoke Star", description: "Fun karaoke performance", icon: "🎶" },
  { id: "concert", label: "Concert Singer", description: "Professional performer vibe", icon: "🎵" },
  { id: "lullaby", label: "Lullaby", description: "Sweet gentle singing", icon: "💤" },
];

const SINGING_AUDIO_SOURCES = [
  { id: "upload", label: "Upload Song", description: "Upload your own audio file", icon: "📁" },
  { id: "video_url", label: "Video URL", description: "Extract audio from video link", icon: "🔗" },
  { id: "preset", label: "Preset Songs", description: "Choose from popular sounds", icon: "🎵" },
];

const PRESET_SONGS = [
  { id: "baby_shark", label: "Baby Shark", description: "Doo doo doo doo", icon: "🦈", mood: "playful" },
  { id: "twinkle_star", label: "Twinkle Twinkle", description: "Little star lullaby", icon: "⭐", mood: "calm" },
  { id: "happy_birthday", label: "Happy Birthday", description: "Celebration classic", icon: "🎂", mood: "celebration" },
  { id: "wheels_bus", label: "Wheels on the Bus", description: "Round and round", icon: "🚌", mood: "playful" },
  { id: "old_macdonald", label: "Old MacDonald", description: "E-I-E-I-O", icon: "🐄", mood: "playful" },
  { id: "abc_song", label: "ABC Song", description: "Learning alphabet", icon: "🔤", mood: "educational" },
];

const SINGING_SETTINGS = [
  { id: "stage", label: "Concert Stage", description: "Big performance", icon: "🎤" },
  { id: "karaoke_room", label: "Karaoke Room", description: "Colorful neon vibes", icon: "🎶" },
  { id: "bedroom", label: "Kids Bedroom", description: "Cozy home performance", icon: "🛏️" },
  { id: "music_studio", label: "Recording Studio", description: "Professional setting", icon: "🎧" },
  { id: "outdoor", label: "Outdoor Garden", description: "Nature backdrop", icon: "🌸" },
];

// ===== FUTURE ME OPTIONS =====
const FUTURE_ACTIVITIES = [
  { id: "dancing", label: "Dancing", description: "Joyful dancing", icon: "💃" },
  { id: "walking", label: "Walking", description: "Graceful walking", icon: "🚶" },
  { id: "waving", label: "Waving Hello", description: "Warm greeting", icon: "👋" },
  { id: "sitting", label: "Sitting Peacefully", description: "Relaxed & content", icon: "🪑" },
  { id: "exercising", label: "Exercising", description: "Staying active", icon: "🏃" },
];

const FUTURE_SETTINGS = [
  { id: "beach", label: "Beach Sunset", description: "Peaceful retirement", icon: "🏖️" },
  { id: "garden", label: "Garden", description: "Among flowers", icon: "🌸" },
  { id: "living_room", label: "Cozy Home", description: "Warm & familiar", icon: "🏠" },
  { id: "park", label: "Park Bench", description: "Nature & peace", icon: "🌳" },
  { id: "cafe", label: "Outdoor Café", description: "Enjoying life", icon: "☕" },
];

// ===== ANIME ME OPTIONS =====
const ANIME_STYLES = [
  { id: "anime", label: "Anime", description: "Japanese anime style", icon: "🎌" },
  { id: "pixar", label: "Pixar/Disney", description: "3D animation style", icon: "✨" },
  { id: "comic", label: "Comic Book", description: "Bold superhero style", icon: "💥" },
  { id: "ghibli", label: "Studio Ghibli", description: "Soft watercolor", icon: "🍃" },
  { id: "simpsons", label: "Simpsons Style", description: "Yellow cartoon", icon: "📺" },
];

const ANIME_ACTIONS = [
  { id: "power_pose", label: "Power Pose", description: "Dramatic energy", icon: "⚡" },
  { id: "running", label: "Running", description: "Dynamic motion", icon: "🏃" },
  { id: "fighting", label: "Battle Stance", description: "Ready for action", icon: "⚔️" },
  { id: "cute_wave", label: "Cute Wave", description: "Kawaii greeting", icon: "✋" },
  { id: "victory", label: "Victory Pose", description: "Celebrating win", icon: "🏆" },
];

// ===== PET TWIN OPTIONS =====
const PET_TYPES = [
  { id: "golden_retriever", label: "Golden Retriever", description: "Fluffy & friendly", icon: "🐕" },
  { id: "husky", label: "Husky", description: "Striking eyes", icon: "🐺" },
  { id: "corgi", label: "Corgi", description: "Adorable short legs", icon: "🐶" },
  { id: "pug", label: "Pug", description: "Cute & wrinkly", icon: "🐕‍🦺" },
  { id: "persian_cat", label: "Persian Cat", description: "Fluffy & elegant", icon: "🐱" },
  { id: "tabby_cat", label: "Tabby Cat", description: "Classic stripes", icon: "🐈" },
  { id: "shiba", label: "Shiba Inu", description: "Fox-like doge", icon: "🦊" },
];

const PET_ACTIONS = [
  { id: "playing", label: "Playing", description: "Happy & playful", icon: "🎾" },
  { id: "running", label: "Running", description: "Ears flopping", icon: "🏃" },
  { id: "sleeping", label: "Sleeping Cute", description: "Curled up cozy", icon: "😴" },
  { id: "head_tilt", label: "Head Tilt", description: "Confused & cute", icon: "🤔" },
  { id: "sitting", label: "Sitting Pretty", description: "Posing nicely", icon: "🐕" },
];

// ===== TIME TRAVELER OPTIONS =====
const DECADES = [
  { id: "70s", label: "70s Disco", description: "Bell bottoms & gold", icon: "🪩" },
  { id: "80s", label: "80s Neon", description: "Synth-wave vibes", icon: "📼" },
  { id: "90s", label: "90s Grunge", description: "Flannel & attitude", icon: "🎸" },
  { id: "2000s", label: "2000s Y2K", description: "Low-rise & bling", icon: "💿" },
  { id: "60s", label: "60s Hippie", description: "Peace & flowers", icon: "✌️" },
];

const DECADE_SETTINGS = [
  { id: "dance_floor", label: "Dance Floor", description: "Era lighting", icon: "💃" },
  { id: "street", label: "Street Scene", description: "Period cars", icon: "🚗" },
  { id: "concert", label: "Concert", description: "Era band", icon: "🎤" },
  { id: "mall", label: "Mall/Arcade", description: "Shopping vibes", icon: "🛍️" },
];

// ===== ACTION HERO OPTIONS =====
const HERO_TYPES = [
  { id: "superhero", label: "Superhero", description: "Cape & powers", icon: "🦸" },
  { id: "sports_star", label: "Sports Star", description: "Athletic champion", icon: "🏆" },
  { id: "spy", label: "Secret Spy", description: "Suave agent", icon: "🕵️" },
  { id: "martial_artist", label: "Martial Artist", description: "Combat master", icon: "🥋" },
  { id: "action_movie", label: "Action Star", description: "Movie hero", icon: "🎬" },
];

const HERO_ACTIONS = [
  { id: "flying", label: "Flying/Leaping", description: "Through the air", icon: "🦅" },
  { id: "fighting", label: "Fighting", description: "Epic battle", icon: "⚔️" },
  { id: "scoring", label: "Scoring Goal", description: "Winning moment", icon: "⚽" },
  { id: "dramatic_walk", label: "Dramatic Walk", description: "Explosions behind", icon: "💥" },
  { id: "power_up", label: "Powering Up", description: "Energy surge", icon: "⚡" },
];

const HERO_SETTINGS = [
  { id: "city", label: "City Skyline", description: "Urban hero", icon: "🏙️" },
  { id: "stadium", label: "Stadium", description: "Cheering crowd", icon: "🏟️" },
  { id: "explosion", label: "Explosion BG", description: "Action movie", icon: "💥" },
  { id: "training", label: "Training Ground", description: "Epic dojo", icon: "🥋" },
];

// ===== TRANSFORMATION OPTIONS =====
const TRANSFORMATION_PRESETS = [
  { id: "custom", label: "Custom Transformation", description: "Describe your own transformation", icon: "✨" },
  { id: "car_transformer", label: "Car to Transformer", description: "Vehicle transforms into robot", icon: "🤖" },
  { id: "object_creature", label: "Object to Creature", description: "Everyday item becomes alive", icon: "🦋" },
  { id: "human_superhero", label: "Human to Superhero", description: "Ordinary to extraordinary", icon: "🦸" },
  { id: "old_to_new", label: "Old to New", description: "Restoration transformation", icon: "🔄" },
  { id: "sketch_reality", label: "Sketch to Reality", description: "Drawing comes to life", icon: "✏️" },
  { id: "day_night", label: "Day to Night", description: "Scene time transformation", icon: "🌙" },
  { id: "season_change", label: "Season Change", description: "Summer to winter, etc", icon: "❄️" },
];

const TRANSFORMATION_ACTIONS = [
  { id: "morph", label: "Smooth Morph", description: "Gradual transformation", icon: "🔮" },
  { id: "fly_away", label: "Fly Away", description: "Takes off and flies", icon: "🚀" },
  { id: "explode_reform", label: "Explode & Reform", description: "Breaks apart and reforms", icon: "💥" },
  { id: "spin_transform", label: "Spin Transform", description: "Spins while changing", icon: "🌀" },
  { id: "grow_shrink", label: "Grow/Shrink", description: "Size transformation", icon: "📏" },
  { id: "teleport", label: "Teleport", description: "Disappear and reappear", icon: "⚡" },
  { id: "unfold", label: "Unfold/Assemble", description: "Pieces come together", icon: "🔧" },
];

const TRANSFORMATION_DESTINATIONS = [
  { id: "none", label: "Stay in Place", description: "Transform without moving", icon: "📍" },
  { id: "sky", label: "Into the Sky", description: "Flies up into clouds", icon: "☁️" },
  { id: "building", label: "To Building Top", description: "Lands on skyscraper", icon: "🏢" },
  { id: "space", label: "Into Space", description: "Rockets to space", icon: "🌌" },
  { id: "city", label: "Through City", description: "Navigates urban landscape", icon: "🏙️" },
  { id: "water", label: "Into Water", description: "Dives into ocean", icon: "🌊" },
];

const TRANSFORMATION_STYLES = [
  { id: "cinematic", label: "Cinematic", description: "Movie-quality effects", icon: "🎬" },
  { id: "cartoon", label: "Cartoon", description: "Animated style", icon: "🎨" },
  { id: "realistic", label: "Hyper-Realistic", description: "Lifelike transformation", icon: "📸" },
  { id: "sci_fi", label: "Sci-Fi", description: "Futuristic tech style", icon: "🛸" },
  { id: "fantasy", label: "Fantasy/Magic", description: "Magical sparkles", icon: "✨" },
];

// Music moods (shared)
const MUSIC_MOODS = [
  { id: "upbeat", label: "Upbeat & Fun", icon: "🎵" },
  { id: "trendy", label: "TikTok Trending", icon: "🔥" },
  { id: "epic", label: "Epic Cinematic", icon: "🎬" },
  { id: "nostalgic", label: "Nostalgic", icon: "📻" },
];

// Video durations
const DURATIONS = [
  { id: "5", label: "5 sec", description: "Quick clip" },
  { id: "10", label: "10 sec", description: "Standard" },
  { id: "15", label: "15 sec", description: "Extended" },
];

// Selection Button Component
const SelectBtn = ({ selected, onClick, children, className = "" }) => (
  <button
    onClick={onClick}
    className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
      selected
        ? "bg-gradient-to-r from-pink-500/20 to-purple-500/20 border-pink-500 text-pink-400 border-2 shadow-lg shadow-pink-500/10"
        : "bg-white/5 border-white/10 text-white/70 border hover:bg-white/10 hover:text-white hover:border-white/20"
    } ${className}`}
  >
    {children}
  </button>
);

export default function ViralMakerPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  
  // Form state - supports all templates
  const [form, setForm] = useState({
    template: "dancing_baby",
    faceImage: null,
    faceImagePreview: null,
    musicMood: "upbeat",
    duration: "10",
    // Dancing Baby
    danceStyle: "cute_bounce",
    costume: "classic_baby",
    setting: "colorful",
    // Singing Baby
    singingStyle: "cute_sing",
    audioSource: "preset",
    presetSong: "baby_shark",
    audioFile: null,
    audioFileName: null,
    videoUrl: "",
    singingSetting: "stage",
    // Future Me
    futureActivity: "dancing",
    futureSetting: "beach",
    // Anime Me
    animeStyle: "anime",
    animeAction: "power_pose",
    // Pet Twin
    petType: "golden_retriever",
    petAction: "playing",
    // Time Traveler
    decade: "80s",
    decadeSetting: "dance_floor",
    // Action Hero
    heroType: "superhero",
    heroAction: "flying",
    heroSetting: "city",
    // Transformation (NEW)
    transformPreset: "custom",
    transformFromDescription: "", // What the source is (if no image uploaded)
    transformToDescription: "", // What to transform into
    transformAction: "morph",
    transformDestination: "none",
    transformStyle: "cinematic",
    transformedImageUrl: null, // Preview of transformed image before video
  });
  
  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [videoId, setVideoId] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  
  // Current step (1-5: Template, Face, Style/Options, Customize, Create)
  const [step, setStep] = useState(1);
  
  // Upgrade modal
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeErrorType, setUpgradeErrorType] = useState(null);
  const [upgradeErrorData, setUpgradeErrorData] = useState(null);
  
  // Update form helper
  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  
  // Get current template info
  const currentTemplate = VIRAL_TEMPLATES.find(t => t.id === form.template) || VIRAL_TEMPLATES[0];

  // Handle face image upload
  const handleFaceImageUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file && file.type.includes("image")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        updateForm("faceImage", file);
        updateForm("faceImagePreview", e.target.result);
        toast.success(`Photo uploaded! AI will create your ${currentTemplate.label}.`);
      };
      reader.readAsDataURL(file);
    }
  }, [currentTemplate]);

  // Poll for video progress
  useEffect(() => {
    if (!videoId || !isGenerating) return;
    
    let pollInterval = null;
    
    // Initial delay before first poll to ensure DB record is written
    const initialDelay = setTimeout(() => {
      pollInterval = setInterval(async () => {
        try {
          const response = await videosAPI.getVideoProgress(videoId);
          const data = response.data;
          
          if (data.progress_percent) setProgress(data.progress_percent);
          if (data.progress) setStatus(data.progress);
          
          if (data.status === "pending" || data.status === "completed") {
            clearInterval(pollInterval);
            setVideoUrl(data.video_url || data.cloud_video_url);
            setProgress(100);
            setStatus("Your viral video is ready!");
            setIsGenerating(false);
            toast.success(`Your ${currentTemplate.label} video is ready to share!`);
          } else if (data.status === "failed") {
            // Only show failure if progress is 0 or error is explicitly set
            if (data.progress_percent === 0 || data.error) {
              clearInterval(pollInterval);
              setIsGenerating(false);
              toast.error(data.error || "Generation failed");
            } else {
              console.log("Ignoring transient failed status, continuing to poll...");
            }
          }
        } catch (err) {
          if (err.response?.status !== 404) {
            console.error("Poll error:", err);
          }
        }
      }, 8000);
    }, 2000);
    
    return () => {
      clearTimeout(initialDelay);
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [videoId, isGenerating, currentTemplate]);
  
  // Generate viral video - supports all templates
  const generateVideo = async () => {
    // Validation - Transformation can work with description instead of image
    if (form.template === "transformation") {
      if (!form.faceImage && !form.transformFromDescription) {
        toast.error("Please upload a source image or describe what to transform");
        return;
      }
      if (!form.transformToDescription) {
        toast.error("Please describe what to transform into");
        return;
      }
    } else if (!form.faceImage) {
      toast.error("Please upload a face photo first");
      return;
    }
    
    setIsGenerating(true);
    setProgress(5);
    setStatus(`Analyzing your face for ${currentTemplate.label}...`);
    setVideoUrl(null);
    
    try {
      // Build template-specific params
      const params = {
        faceImage: form.faceImage,
        template: form.template,
        musicMood: form.musicMood,
        duration: form.duration,
      };
      
      // Add template-specific options
      if (form.template === "dancing_baby") {
        params.style = form.danceStyle;
        params.costume = form.costume;
        params.setting = form.setting;
      } else if (form.template === "singing_baby") {
        params.style = form.singingStyle;
        params.setting = form.singingSetting;
        params.audioSource = form.audioSource;
        params.presetSong = form.presetSong;
        params.videoUrl = form.videoUrl;
        // Audio file will be sent separately via FormData
        if (form.audioSource === "upload" && form.audioFile) {
          params.hasAudioFile = true;
        }
      } else if (form.template === "future_me") {
        params.action = form.futureActivity;
        params.setting = form.futureSetting;
      } else if (form.template === "anime_me") {
        params.style = form.animeStyle;
        params.action = form.animeAction;
      } else if (form.template === "pet_twin") {
        params.petType = form.petType;
        params.action = form.petAction;
      } else if (form.template === "time_traveler") {
        params.decade = form.decade;
        params.setting = form.decadeSetting;
      } else if (form.template === "action_hero") {
        params.heroType = form.heroType;
        params.action = form.heroAction;
        params.setting = form.heroSetting;
      } else if (form.template === "transformation") {
        params.transformFromDescription = form.transformFromDescription;
        params.transformToDescription = form.transformToDescription;
        params.transformAction = form.transformAction;
        params.transformDestination = form.transformDestination;
        params.transformStyle = form.transformStyle;
        params.transformPreset = form.transformPreset;
      }
      
      // Add audio file for singing baby
      if (form.template === "singing_baby" && form.audioSource === "upload" && form.audioFile) {
        params.audioFile = form.audioFile;
      }
      
      const response = await ugcAPI.generateViralVideo(params);
      
      if (response.videoId) {
        setVideoId(response.videoId);
        setStatus(`Creating your ${currentTemplate.label} (3-5 min)...`);
        setProgress(15);
      }
    } catch (error) {
      console.error("Generation error:", error);
      
      // Check for subscription quota errors (403)
      if (error.response?.status === 403) {
        const detail = error.response.data?.detail;
        if (detail && typeof detail === 'object') {
          const errorType = detail.error || 'ugc_access_required';
          setUpgradeErrorType(errorType);
          setUpgradeErrorData(detail);
          setUpgradeModalOpen(true);
          setIsGenerating(false);
          return;
        }
      }
      
      const errorMessage = error.response?.data?.detail;
      if (typeof errorMessage === 'string') {
        toast.error(errorMessage);
      } else {
        toast.error("Generation failed. Please try again.");
      }
      setIsGenerating(false);
    }
  };
  
  // Video controls
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };
  
  // Download video
  const downloadVideo = () => {
    if (videoUrl) {
      const a = document.createElement("a");
      a.href = videoUrl;
      a.download = `${form.template}-viral-${Date.now()}.mp4`;
      a.click();
      toast.success("Download started!");
    }
  };
  
  // Step navigation (now 5 steps: Template, Face, Style, Customize, Create)
  const nextStep = () => step < 5 && setStep(step + 1);
  const prevStep = () => step > 1 && setStep(step - 1);
  
  // Check if step is complete
  const isStepComplete = (s) => {
    switch (s) {
      case 1: return true; // Template always selected
      case 2: return !!form.faceImage;
      case 3: return true; // Style always has default
      case 4: return true; // Customize always has default
      case 5: return true;
      default: return false;
    }
  };
  
  // Get step labels based on template
  const getStepLabels = () => {
    const base = ["Trend", "Face"];
    if (form.template === "dancing_baby") return [...base, "Dance", "Look", "Create"];
    if (form.template === "singing_baby") return [...base, "Style", "Audio", "Create"];
    if (form.template === "future_me") return [...base, "Activity", "Setting", "Create"];
    if (form.template === "anime_me") return [...base, "Style", "Action", "Create"];
    if (form.template === "pet_twin") return [...base, "Pet", "Action", "Create"];
    if (form.template === "time_traveler") return [...base, "Decade", "Setting", "Create"];
    if (form.template === "action_hero") return [...base, "Hero", "Action", "Create"];
    if (form.template === "transformation") return ["Trend", "Source", "Transform", "Action", "Create"];
    return [...base, "Style", "Options", "Create"];
  };
  
  const stepLabels = getStepLabels();
  
  return (
    <div className="min-h-screen bg-[#0A0A0B] p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br from-${currentTemplate.color}-500 to-purple-600 flex items-center justify-center`}>
            <span className="text-2xl">{currentTemplate.icon}</span>
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-2">
              Viral Maker
              <span className="text-xs bg-gradient-to-r from-pink-500 to-purple-500 text-white px-2 py-1 rounded-full font-medium">
                7 TRENDS
              </span>
            </h1>
            <p className="text-white/50 text-sm">Create viral AI transformation videos</p>
          </div>
        </div>
        
        {/* Current Template Badge */}
        <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/20">
          <TrendingUp className="w-4 h-4 text-pink-400" />
          <span className="text-pink-400 text-sm font-medium">{currentTemplate.views} views</span>
          <span className="text-white/40 text-sm">|</span>
          <span className="text-white/60 text-sm">{currentTemplate.label}</span>
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Left: Form Steps */}
          <div>
            {/* Step Indicator */}
            <div className="flex items-center gap-1 mb-4 overflow-x-auto">
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  key={s}
                  onClick={() => setStep(s)}
                  className={`flex items-center gap-1 px-2 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                    step === s
                      ? "bg-gradient-to-r from-pink-500 to-purple-500 text-white"
                      : isStepComplete(s)
                      ? "bg-white/10 text-white"
                      : "bg-white/5 text-white/40"
                  }`}
                >
                  {isStepComplete(s) && step !== s ? (
                    <Check className="w-3 h-3" />
                  ) : (
                    <span>{s}</span>
                  )}
                  <span className="hidden sm:inline">{stepLabels[s-1]}</span>
                </button>
              ))}
            </div>
            
            {/* Step Content */}
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-4 md:p-6">
              <AnimatePresence mode="wait">
                {/* Step 1: Choose Template */}
                {step === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="space-y-4"
                  >
                    <div className="text-center mb-4">
                      <h2 className="text-xl font-bold text-white mb-2">Choose Your Viral Trend</h2>
                      <p className="text-white/50 text-sm">
                        Pick a trending AI transformation to create
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      {VIRAL_TEMPLATES.map((template) => (
                        <button
                          key={template.id}
                          onClick={() => updateForm("template", template.id)}
                          data-testid={`template-${template.id}`}
                          className={`p-4 rounded-xl border-2 text-left transition-all relative ${
                            form.template === template.id
                              ? `bg-gradient-to-br from-${template.color}-500/20 to-purple-500/20 border-${template.color}-500 shadow-lg`
                              : "bg-white/5 border-white/10 hover:border-white/20"
                          }`}
                        >
                          {template.trending && (
                            <span className="absolute top-2 right-2 text-[10px] bg-pink-500/80 text-white px-1.5 py-0.5 rounded-full">
                              HOT
                            </span>
                          )}
                          <div className="text-2xl mb-2">{template.icon}</div>
                          <div className={`font-medium ${form.template === template.id ? `text-${template.color}-400` : "text-white"}`}>
                            {template.label}
                          </div>
                          <div className="text-xs text-white/50 mt-1">{template.description}</div>
                          <div className="text-[10px] text-white/30 mt-2">{template.views}</div>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
                
                {/* Step 2: Upload Face */}
                {step === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="space-y-4"
                  >
                    <div className="text-center mb-4">
                      <h2 className="text-xl font-bold text-white mb-2">Upload Your Face</h2>
                      <p className="text-white/50 text-sm">
                        AI will transform you into {currentTemplate.label.toLowerCase()}
                      </p>
                    </div>
                    
                    {/* Face Image Upload */}
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className={`relative border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
                        form.faceImagePreview
                          ? "border-pink-500/50 bg-pink-500/5"
                          : "border-white/20 hover:border-pink-500/50 hover:bg-white/5"
                      }`}
                    >
                      {form.faceImagePreview ? (
                        <div className="relative">
                          <img
                            src={form.faceImagePreview}
                            alt="Your face"
                            className="w-48 h-48 mx-auto rounded-2xl object-cover border-4 border-pink-500/30"
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateForm("faceImage", null);
                              updateForm("faceImagePreview", null);
                            }}
                            className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 rounded-full p-2 text-white shadow-lg"
                          >
                            <X className="w-4 h-4" />
                          </button>
                          <div className="mt-4 flex items-center justify-center gap-2 text-pink-400">
                            <Check className="w-5 h-5" />
                            <span className="font-medium">Photo uploaded!</span>
                          </div>
                        </div>
                      ) : (
                        <div className="py-8">
                          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-pink-500/20 to-purple-500/20 flex items-center justify-center mb-4">
                            <Camera className="w-10 h-10 text-pink-400" />
                          </div>
                          <p className="text-white font-medium mb-2">Drop your photo here</p>
                          <p className="text-white/50 text-sm">or click to upload</p>
                          <p className="text-white/30 text-xs mt-2">Best: Clear face photo, front-facing</p>
                        </div>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFaceImageUpload}
                      className="hidden"
                    />
                    
                    {/* Media Library Picker */}
                    {!form.faceImagePreview && (
                      <MediaPickerButton
                        onSelect={(data) => {
                          if (data.url || data.thumbnail_url) {
                            const imageUrl = data.url || data.thumbnail_url;
                            fetch(imageUrl)
                              .then(res => res.blob())
                              .then(blob => {
                                const file = new File([blob], data.name || "face.jpg", { type: blob.type || "image/jpeg" });
                                updateForm("faceImage", file);
                                updateForm("faceImagePreview", imageUrl);
                                toast.success("Image selected from Media Library");
                              })
                              .catch(() => toast.error("Failed to load image"));
                          } else if (data.isUpload && data.file) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              updateForm("faceImage", data.file);
                              updateForm("faceImagePreview", reader.result);
                              toast.success("Image uploaded");
                            };
                            reader.readAsDataURL(data.file);
                          }
                        }}
                        mediaType="image"
                        buttonText="Or pick from Media Library"
                        buttonClassName="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-xl text-sm text-purple-300 transition-colors"
                      />
                    )}
                    
                    {/* Tips */}
                    <div className="bg-gradient-to-r from-pink-500/5 to-purple-500/5 rounded-xl p-4 border border-pink-500/10">
                      <p className="text-pink-400 text-sm font-medium mb-2">Pro Tips:</p>
                      <ul className="text-white/60 text-xs space-y-1">
                        <li>- Use a clear, well-lit photo</li>
                        <li>- Face should be looking at the camera</li>
                        <li>- Works best with neutral expression</li>
                      </ul>
                    </div>
                  </motion.div>
                )}
                
                {/* Step 3: Template-Specific Style Options */}
                {step === 3 && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="space-y-5"
                  >
                    {/* Dancing Baby - Dance Style */}
                    {form.template === "dancing_baby" && (
                      <>
                        <div className="text-center mb-4">
                          <h2 className="text-xl font-bold text-white mb-2">Choose Dance Style</h2>
                          <p className="text-white/50 text-sm">Pick how your mini-you will dance</p>
                        </div>
                        <div>
                          <Label className="text-white/70 text-xs mb-3 block">Dance Moves</Label>
                          <div className="grid grid-cols-2 gap-3">
                            {BABY_DANCE_STYLES.map((style) => (
                              <button
                                key={style.id}
                                onClick={() => updateForm("danceStyle", style.id)}
                                className={`p-4 rounded-xl border-2 text-left transition-all ${
                                  form.danceStyle === style.id
                                    ? "bg-gradient-to-br from-pink-500/20 to-purple-500/20 border-pink-500 shadow-lg"
                                    : "bg-white/5 border-white/10 hover:border-white/20"
                                }`}
                                data-testid={`dance-style-${style.id}`}
                              >
                                <div className="text-2xl mb-2">{style.icon}</div>
                                <div className={`font-medium ${form.danceStyle === style.id ? "text-pink-400" : "text-white"}`}>
                                  {style.label}
                                </div>
                                <div className="text-xs text-white/50 mt-1">{style.description}</div>
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                    
                    {/* Singing Baby - Style */}
                    {form.template === "singing_baby" && (
                      <>
                        <div className="text-center mb-4">
                          <h2 className="text-xl font-bold text-white mb-2">Choose Singing Style</h2>
                          <p className="text-white/50 text-sm">Pick how your baby will perform</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {SINGING_STYLES.map((style) => (
                            <button
                              key={style.id}
                              onClick={() => updateForm("singingStyle", style.id)}
                              className={`p-4 rounded-xl border-2 text-left transition-all ${
                                form.singingStyle === style.id
                                  ? "bg-gradient-to-br from-rose-500/20 to-pink-500/20 border-rose-500 shadow-lg"
                                  : "bg-white/5 border-white/10 hover:border-white/20"
                              }`}
                              data-testid={`singing-style-${style.id}`}
                            >
                              <div className="text-2xl mb-2">{style.icon}</div>
                              <div className={`font-medium ${form.singingStyle === style.id ? "text-rose-400" : "text-white"}`}>
                                {style.label}
                              </div>
                              <div className="text-xs text-white/50 mt-1">{style.description}</div>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                    
                    {/* Future Me - Activity */}
                    {form.template === "future_me" && (
                      <>
                        <div className="text-center mb-4">
                          <h2 className="text-xl font-bold text-white mb-2">Choose Activity</h2>
                          <p className="text-white/50 text-sm">What will your future self be doing?</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {FUTURE_ACTIVITIES.map((activity) => (
                            <button
                              key={activity.id}
                              onClick={() => updateForm("futureActivity", activity.id)}
                              className={`p-4 rounded-xl border-2 text-left transition-all ${
                                form.futureActivity === activity.id
                                  ? "bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-amber-500 shadow-lg"
                                  : "bg-white/5 border-white/10 hover:border-white/20"
                              }`}
                            >
                              <div className="text-2xl mb-2">{activity.icon}</div>
                              <div className={`font-medium ${form.futureActivity === activity.id ? "text-amber-400" : "text-white"}`}>
                                {activity.label}
                              </div>
                              <div className="text-xs text-white/50 mt-1">{activity.description}</div>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                    
                    {/* Anime Me - Style */}
                    {form.template === "anime_me" && (
                      <>
                        <div className="text-center mb-4">
                          <h2 className="text-xl font-bold text-white mb-2">Choose Anime Style</h2>
                          <p className="text-white/50 text-sm">Pick your animation aesthetic</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {ANIME_STYLES.map((style) => (
                            <button
                              key={style.id}
                              onClick={() => updateForm("animeStyle", style.id)}
                              className={`p-4 rounded-xl border-2 text-left transition-all ${
                                form.animeStyle === style.id
                                  ? "bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-500 shadow-lg"
                                  : "bg-white/5 border-white/10 hover:border-white/20"
                              }`}
                            >
                              <div className="text-2xl mb-2">{style.icon}</div>
                              <div className={`font-medium ${form.animeStyle === style.id ? "text-purple-400" : "text-white"}`}>
                                {style.label}
                              </div>
                              <div className="text-xs text-white/50 mt-1">{style.description}</div>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                    
                    {/* Pet Twin - Pet Type */}
                    {form.template === "pet_twin" && (
                      <>
                        <div className="text-center mb-4">
                          <h2 className="text-xl font-bold text-white mb-2">Choose Your Pet</h2>
                          <p className="text-white/50 text-sm">Pick the pet that matches your vibe</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {PET_TYPES.map((pet) => (
                            <button
                              key={pet.id}
                              onClick={() => updateForm("petType", pet.id)}
                              className={`p-3 rounded-xl border-2 text-left transition-all ${
                                form.petType === pet.id
                                  ? "bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border-emerald-500 shadow-lg"
                                  : "bg-white/5 border-white/10 hover:border-white/20"
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-xl">{pet.icon}</span>
                                <div>
                                  <div className={`font-medium text-sm ${form.petType === pet.id ? "text-emerald-400" : "text-white"}`}>
                                    {pet.label}
                                  </div>
                                  <div className="text-[10px] text-white/50">{pet.description}</div>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                    
                    {/* Time Traveler - Decade */}
                    {form.template === "time_traveler" && (
                      <>
                        <div className="text-center mb-4">
                          <h2 className="text-xl font-bold text-white mb-2">Choose Your Era</h2>
                          <p className="text-white/50 text-sm">Travel back in time!</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {DECADES.map((decade) => (
                            <button
                              key={decade.id}
                              onClick={() => updateForm("decade", decade.id)}
                              className={`p-4 rounded-xl border-2 text-left transition-all ${
                                form.decade === decade.id
                                  ? "bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border-cyan-500 shadow-lg"
                                  : "bg-white/5 border-white/10 hover:border-white/20"
                              }`}
                            >
                              <div className="text-2xl mb-2">{decade.icon}</div>
                              <div className={`font-medium ${form.decade === decade.id ? "text-cyan-400" : "text-white"}`}>
                                {decade.label}
                              </div>
                              <div className="text-xs text-white/50 mt-1">{decade.description}</div>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                    
                    {/* Action Hero - Hero Type */}
                    {form.template === "action_hero" && (
                      <>
                        <div className="text-center mb-4">
                          <h2 className="text-xl font-bold text-white mb-2">Choose Hero Type</h2>
                          <p className="text-white/50 text-sm">What kind of hero will you become?</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {HERO_TYPES.map((hero) => (
                            <button
                              key={hero.id}
                              onClick={() => updateForm("heroType", hero.id)}
                              className={`p-4 rounded-xl border-2 text-left transition-all ${
                                form.heroType === hero.id
                                  ? "bg-gradient-to-br from-red-500/20 to-orange-500/20 border-red-500 shadow-lg"
                                  : "bg-white/5 border-white/10 hover:border-white/20"
                              }`}
                            >
                              <div className="text-2xl mb-2">{hero.icon}</div>
                              <div className={`font-medium ${form.heroType === hero.id ? "text-red-400" : "text-white"}`}>
                                {hero.label}
                              </div>
                              <div className="text-xs text-white/50 mt-1">{hero.description}</div>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                    
                    {/* Transformation - Step 3: What to Transform Into */}
                    {form.template === "transformation" && (
                      <>
                        <div className="text-center mb-4">
                          <h2 className="text-xl font-bold text-white mb-2">🔮 Transformation Setup</h2>
                          <p className="text-white/50 text-sm">Describe the transformation you want to create</p>
                        </div>
                        
                        {/* Preset Quick Options */}
                        <div className="mb-4">
                          <Label className="text-white/70 text-xs mb-3 block">Quick Presets</Label>
                          <div className="grid grid-cols-2 gap-2">
                            {TRANSFORMATION_PRESETS.slice(0, 4).map((preset) => (
                              <button
                                key={preset.id}
                                onClick={() => {
                                  updateForm("transformPreset", preset.id);
                                  if (preset.id === "car_transformer") {
                                    updateForm("transformToDescription", "a giant robot transformer with glowing eyes and metallic armor");
                                    updateForm("transformAction", "unfold");
                                    updateForm("transformDestination", "building");
                                  } else if (preset.id === "object_creature") {
                                    updateForm("transformToDescription", "a magical living creature with expressive eyes and fluid movement");
                                    updateForm("transformAction", "morph");
                                  } else if (preset.id === "human_superhero") {
                                    updateForm("transformToDescription", "a powerful superhero with glowing energy aura and cape flowing");
                                    updateForm("transformAction", "spin_transform");
                                    updateForm("transformDestination", "sky");
                                  }
                                }}
                                className={`p-3 rounded-xl border-2 text-left transition-all ${
                                  form.transformPreset === preset.id
                                    ? "bg-gradient-to-br from-violet-500/20 to-purple-500/20 border-violet-500 shadow-lg"
                                    : "bg-white/5 border-white/10 hover:border-white/20"
                                }`}
                                data-testid={`transform-preset-${preset.id}`}
                              >
                                <div className="text-xl mb-1">{preset.icon}</div>
                                <div className={`font-medium text-sm ${form.transformPreset === preset.id ? "text-violet-400" : "text-white"}`}>
                                  {preset.label}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        {/* Source Description (if no image uploaded) */}
                        {!form.faceImagePreview && (
                          <div className="mb-4">
                            <Label className="text-white/70 text-xs mb-2 block">
                              Describe the Starting Object (or upload an image in Step 2)
                            </Label>
                            <textarea
                              value={form.transformFromDescription}
                              onChange={(e) => updateForm("transformFromDescription", e.target.value)}
                              placeholder="E.g., A red sports car parked on a city street..."
                              className="w-full p-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 text-sm resize-none focus:border-violet-500 focus:outline-none"
                              rows={2}
                              data-testid="transform-from-input"
                            />
                          </div>
                        )}
                        
                        {/* Transform Into Description */}
                        <div className="mb-4">
                          <Label className="text-white/70 text-xs mb-2 block">Transform Into (describe the result)</Label>
                          <textarea
                            value={form.transformToDescription}
                            onChange={(e) => updateForm("transformToDescription", e.target.value)}
                            placeholder="E.g., A giant robot transformer standing tall, metallic with glowing blue eyes..."
                            className="w-full p-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 text-sm resize-none focus:border-violet-500 focus:outline-none"
                            rows={3}
                            data-testid="transform-to-input"
                          />
                        </div>
                        
                        {/* Transformation Style */}
                        <div>
                          <Label className="text-white/70 text-xs mb-3 block">Visual Style</Label>
                          <div className="flex flex-wrap gap-2">
                            {TRANSFORMATION_STYLES.map((style) => (
                              <SelectBtn
                                key={style.id}
                                selected={form.transformStyle === style.id}
                                onClick={() => updateForm("transformStyle", style.id)}
                              >
                                {style.icon} {style.label}
                              </SelectBtn>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                    
                    {/* Music & Duration (shared across templates - hide for transformation) */}
                    <div>
                      <Label className="text-white/70 text-xs mb-3 block">Music Vibe</Label>
                      <div className="flex flex-wrap gap-2">
                        {MUSIC_MOODS.map((mood) => (
                          <SelectBtn
                            key={mood.id}
                            selected={form.musicMood === mood.id}
                            onClick={() => updateForm("musicMood", mood.id)}
                          >
                            {mood.icon} {mood.label}
                          </SelectBtn>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-white/70 text-xs mb-3 block">Video Length</Label>
                      <div className="flex gap-3">
                        {DURATIONS.map((d) => (
                          <button
                            key={d.id}
                            onClick={() => updateForm("duration", d.id)}
                            className={`flex-1 p-3 rounded-xl border-2 text-center transition-all ${
                              form.duration === d.id
                                ? "bg-pink-500/20 border-pink-500 text-pink-400"
                                : "bg-white/5 border-white/10 text-white/70 hover:border-white/20"
                            }`}
                          >
                            <div className="font-bold">{d.label}</div>
                            <div className="text-xs text-white/50">{d.description}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
                
                {/* Step 4: Template-Specific Secondary Options */}
                {step === 4 && (
                  <motion.div
                    key="step4"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="space-y-5"
                  >
                    {/* Dancing Baby - Costume & Setting */}
                    {form.template === "dancing_baby" && (
                      <>
                        <div className="text-center mb-4">
                          <h2 className="text-xl font-bold text-white mb-2">Choose the Look</h2>
                          <p className="text-white/50 text-sm">Pick costume and setting</p>
                        </div>
                        <div>
                          <Label className="text-white/70 text-xs mb-3 block">Baby Costume</Label>
                          <div className="grid grid-cols-2 gap-2">
                            {BABY_COSTUMES.map((c) => (
                              <button
                                key={c.id}
                                onClick={() => updateForm("costume", c.id)}
                                className={`p-3 rounded-xl border-2 text-left transition-all ${
                                  form.costume === c.id
                                    ? "bg-gradient-to-br from-pink-500/20 to-purple-500/20 border-pink-500 shadow-lg"
                                    : "bg-white/5 border-white/10 hover:border-white/20"
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-xl">{c.icon}</span>
                                  <div>
                                    <div className={`font-medium text-sm ${form.costume === c.id ? "text-pink-400" : "text-white"}`}>
                                      {c.label}
                                    </div>
                                    <div className="text-[10px] text-white/50">{c.description}</div>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <Label className="text-white/70 text-xs mb-3 block">Background Setting</Label>
                          <div className="grid grid-cols-2 gap-2">
                            {BABY_SETTINGS.map((s) => (
                              <button
                                key={s.id}
                                onClick={() => updateForm("setting", s.id)}
                                className={`p-3 rounded-xl border-2 text-left transition-all ${
                                  form.setting === s.id
                                    ? "bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border-cyan-500 shadow-lg"
                                    : "bg-white/5 border-white/10 hover:border-white/20"
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-xl">{s.icon}</span>
                                  <div>
                                    <div className={`font-medium text-sm ${form.setting === s.id ? "text-cyan-400" : "text-white"}`}>
                                      {s.label}
                                    </div>
                                    <div className="text-[10px] text-white/50">{s.description}</div>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                    
                    {/* Singing Baby - Audio Source & Setting */}
                    {form.template === "singing_baby" && (
                      <>
                        <div className="text-center mb-4">
                          <h2 className="text-xl font-bold text-white mb-2">Choose Audio</h2>
                          <p className="text-white/50 text-sm">Select the song for your baby to sing</p>
                        </div>
                        
                        {/* Audio Source Selection */}
                        <div className="mb-4">
                          <Label className="text-white/70 text-xs mb-3 block">Audio Source</Label>
                          <div className="grid grid-cols-3 gap-2">
                            {SINGING_AUDIO_SOURCES.map((source) => (
                              <button
                                key={source.id}
                                onClick={() => updateForm("audioSource", source.id)}
                                className={`p-3 rounded-xl border-2 text-center transition-all ${
                                  form.audioSource === source.id
                                    ? "bg-gradient-to-br from-rose-500/20 to-pink-500/20 border-rose-500 shadow-lg"
                                    : "bg-white/5 border-white/10 hover:border-white/20"
                                }`}
                                data-testid={`audio-source-${source.id}`}
                              >
                                <div className="text-2xl mb-1">{source.icon}</div>
                                <div className={`font-medium text-xs ${form.audioSource === source.id ? "text-rose-400" : "text-white"}`}>
                                  {source.label}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        {/* Upload Song */}
                        {form.audioSource === "upload" && (
                          <div className="space-y-3">
                            <Label className="text-white/70 text-xs">Upload Audio File</Label>
                            <div className="border-2 border-dashed border-white/20 rounded-xl p-4 text-center">
                              <input
                                type="file"
                                accept="audio/*,.mp3,.wav,.m4a,.aac"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    updateForm("audioFile", file);
                                    updateForm("audioFileName", file.name);
                                  }
                                }}
                                className="hidden"
                                id="audio-upload"
                                data-testid="audio-upload-input"
                              />
                              <label htmlFor="audio-upload" className="cursor-pointer">
                                <div className="text-3xl mb-2">🎵</div>
                                {form.audioFileName ? (
                                  <div className="text-rose-400 font-medium">{form.audioFileName}</div>
                                ) : (
                                  <div className="text-white/50 text-sm">Click to upload MP3, WAV, M4A</div>
                                )}
                              </label>
                            </div>
                          </div>
                        )}
                        
                        {/* Video URL */}
                        {form.audioSource === "video_url" && (
                          <div className="space-y-3">
                            <Label className="text-white/70 text-xs">Paste Video URL</Label>
                            <input
                              type="url"
                              value={form.videoUrl}
                              onChange={(e) => updateForm("videoUrl", e.target.value)}
                              placeholder="https://youtube.com/watch?v=... or TikTok URL"
                              className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white placeholder-white/30"
                              data-testid="video-url-input"
                            />
                            <p className="text-white/40 text-xs">Audio will be extracted from this video</p>
                          </div>
                        )}
                        
                        {/* Preset Songs */}
                        {form.audioSource === "preset" && (
                          <div className="space-y-3">
                            <Label className="text-white/70 text-xs mb-3 block">Choose a Song</Label>
                            <div className="grid grid-cols-2 gap-2">
                              {PRESET_SONGS.map((song) => (
                                <button
                                  key={song.id}
                                  onClick={() => updateForm("presetSong", song.id)}
                                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                                    form.presetSong === song.id
                                      ? "bg-gradient-to-br from-rose-500/20 to-pink-500/20 border-rose-500 shadow-lg"
                                      : "bg-white/5 border-white/10 hover:border-white/20"
                                  }`}
                                  data-testid={`preset-song-${song.id}`}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="text-xl">{song.icon}</span>
                                    <div>
                                      <div className={`font-medium text-sm ${form.presetSong === song.id ? "text-rose-400" : "text-white"}`}>
                                        {song.label}
                                      </div>
                                      <div className="text-[10px] text-white/50">{song.description}</div>
                                    </div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Background Setting */}
                        <div className="mt-4">
                          <Label className="text-white/70 text-xs mb-3 block">Background Setting</Label>
                          <div className="grid grid-cols-2 gap-2">
                            {SINGING_SETTINGS.map((s) => (
                              <button
                                key={s.id}
                                onClick={() => updateForm("singingSetting", s.id)}
                                className={`p-3 rounded-xl border-2 text-left transition-all ${
                                  form.singingSetting === s.id
                                    ? "bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border-cyan-500 shadow-lg"
                                    : "bg-white/5 border-white/10 hover:border-white/20"
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-xl">{s.icon}</span>
                                  <div>
                                    <div className={`font-medium text-sm ${form.singingSetting === s.id ? "text-cyan-400" : "text-white"}`}>
                                      {s.label}
                                    </div>
                                    <div className="text-[10px] text-white/50">{s.description}</div>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                    
                    {/* Future Me - Setting */}
                    {form.template === "future_me" && (
                      <>
                        <div className="text-center mb-4">
                          <h2 className="text-xl font-bold text-white mb-2">Choose Setting</h2>
                          <p className="text-white/50 text-sm">Where will future you be?</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {FUTURE_SETTINGS.map((setting) => (
                            <button
                              key={setting.id}
                              onClick={() => updateForm("futureSetting", setting.id)}
                              className={`p-4 rounded-xl border-2 text-left transition-all ${
                                form.futureSetting === setting.id
                                  ? "bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-amber-500 shadow-lg"
                                  : "bg-white/5 border-white/10 hover:border-white/20"
                              }`}
                            >
                              <div className="text-2xl mb-2">{setting.icon}</div>
                              <div className={`font-medium ${form.futureSetting === setting.id ? "text-amber-400" : "text-white"}`}>
                                {setting.label}
                              </div>
                              <div className="text-xs text-white/50 mt-1">{setting.description}</div>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                    
                    {/* Anime Me - Action */}
                    {form.template === "anime_me" && (
                      <>
                        <div className="text-center mb-4">
                          <h2 className="text-xl font-bold text-white mb-2">Choose Action</h2>
                          <p className="text-white/50 text-sm">Strike a pose!</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {ANIME_ACTIONS.map((action) => (
                            <button
                              key={action.id}
                              onClick={() => updateForm("animeAction", action.id)}
                              className={`p-4 rounded-xl border-2 text-left transition-all ${
                                form.animeAction === action.id
                                  ? "bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-500 shadow-lg"
                                  : "bg-white/5 border-white/10 hover:border-white/20"
                              }`}
                            >
                              <div className="text-2xl mb-2">{action.icon}</div>
                              <div className={`font-medium ${form.animeAction === action.id ? "text-purple-400" : "text-white"}`}>
                                {action.label}
                              </div>
                              <div className="text-xs text-white/50 mt-1">{action.description}</div>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                    
                    {/* Pet Twin - Action */}
                    {form.template === "pet_twin" && (
                      <>
                        <div className="text-center mb-4">
                          <h2 className="text-xl font-bold text-white mb-2">Pet Action</h2>
                          <p className="text-white/50 text-sm">What will your pet twin do?</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {PET_ACTIONS.map((action) => (
                            <button
                              key={action.id}
                              onClick={() => updateForm("petAction", action.id)}
                              className={`p-4 rounded-xl border-2 text-left transition-all ${
                                form.petAction === action.id
                                  ? "bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border-emerald-500 shadow-lg"
                                  : "bg-white/5 border-white/10 hover:border-white/20"
                              }`}
                            >
                              <div className="text-2xl mb-2">{action.icon}</div>
                              <div className={`font-medium ${form.petAction === action.id ? "text-emerald-400" : "text-white"}`}>
                                {action.label}
                              </div>
                              <div className="text-xs text-white/50 mt-1">{action.description}</div>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                    
                    {/* Time Traveler - Setting */}
                    {form.template === "time_traveler" && (
                      <>
                        <div className="text-center mb-4">
                          <h2 className="text-xl font-bold text-white mb-2">Choose Scene</h2>
                          <p className="text-white/50 text-sm">Set the scene for your time travel</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {DECADE_SETTINGS.map((setting) => (
                            <button
                              key={setting.id}
                              onClick={() => updateForm("decadeSetting", setting.id)}
                              className={`p-4 rounded-xl border-2 text-left transition-all ${
                                form.decadeSetting === setting.id
                                  ? "bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border-cyan-500 shadow-lg"
                                  : "bg-white/5 border-white/10 hover:border-white/20"
                              }`}
                            >
                              <div className="text-2xl mb-2">{setting.icon}</div>
                              <div className={`font-medium ${form.decadeSetting === setting.id ? "text-cyan-400" : "text-white"}`}>
                                {setting.label}
                              </div>
                              <div className="text-xs text-white/50 mt-1">{setting.description}</div>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                    
                    {/* Action Hero - Action & Setting */}
                    {form.template === "action_hero" && (
                      <>
                        <div className="text-center mb-4">
                          <h2 className="text-xl font-bold text-white mb-2">Hero Action & Scene</h2>
                          <p className="text-white/50 text-sm">Set up your epic moment</p>
                        </div>
                        <div>
                          <Label className="text-white/70 text-xs mb-3 block">Hero Action</Label>
                          <div className="grid grid-cols-2 gap-2">
                            {HERO_ACTIONS.map((action) => (
                              <button
                                key={action.id}
                                onClick={() => updateForm("heroAction", action.id)}
                                className={`p-3 rounded-xl border-2 text-left transition-all ${
                                  form.heroAction === action.id
                                    ? "bg-gradient-to-br from-red-500/20 to-orange-500/20 border-red-500 shadow-lg"
                                    : "bg-white/5 border-white/10 hover:border-white/20"
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-xl">{action.icon}</span>
                                  <div>
                                    <div className={`font-medium text-sm ${form.heroAction === action.id ? "text-red-400" : "text-white"}`}>
                                      {action.label}
                                    </div>
                                    <div className="text-[10px] text-white/50">{action.description}</div>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <Label className="text-white/70 text-xs mb-3 block">Epic Setting</Label>
                          <div className="grid grid-cols-2 gap-2">
                            {HERO_SETTINGS.map((setting) => (
                              <button
                                key={setting.id}
                                onClick={() => updateForm("heroSetting", setting.id)}
                                className={`p-3 rounded-xl border-2 text-left transition-all ${
                                  form.heroSetting === setting.id
                                    ? "bg-gradient-to-br from-orange-500/20 to-yellow-500/20 border-orange-500 shadow-lg"
                                    : "bg-white/5 border-white/10 hover:border-white/20"
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-xl">{setting.icon}</span>
                                  <div>
                                    <div className={`font-medium text-sm ${form.heroSetting === setting.id ? "text-orange-400" : "text-white"}`}>
                                      {setting.label}
                                    </div>
                                    <div className="text-[10px] text-white/50">{setting.description}</div>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                    
                    {/* Transformation - Step 4: Action & Destination */}
                    {form.template === "transformation" && (
                      <>
                        <div className="text-center mb-4">
                          <h2 className="text-xl font-bold text-white mb-2">🎬 Animation & Action</h2>
                          <p className="text-white/50 text-sm">How should the transformation happen?</p>
                        </div>
                        
                        {/* Transformation Action */}
                        <div className="mb-4">
                          <Label className="text-white/70 text-xs mb-3 block">Transformation Effect</Label>
                          <div className="grid grid-cols-2 gap-2">
                            {TRANSFORMATION_ACTIONS.map((action) => (
                              <button
                                key={action.id}
                                onClick={() => updateForm("transformAction", action.id)}
                                className={`p-3 rounded-xl border-2 text-left transition-all ${
                                  form.transformAction === action.id
                                    ? "bg-gradient-to-br from-violet-500/20 to-purple-500/20 border-violet-500 shadow-lg"
                                    : "bg-white/5 border-white/10 hover:border-white/20"
                                }`}
                                data-testid={`transform-action-${action.id}`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-xl">{action.icon}</span>
                                  <div>
                                    <div className={`font-medium text-sm ${form.transformAction === action.id ? "text-violet-400" : "text-white"}`}>
                                      {action.label}
                                    </div>
                                    <div className="text-[10px] text-white/50">{action.description}</div>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        {/* Destination/What Happens After */}
                        <div>
                          <Label className="text-white/70 text-xs mb-3 block">After Transformation (Optional Action)</Label>
                          <div className="grid grid-cols-2 gap-2">
                            {TRANSFORMATION_DESTINATIONS.map((dest) => (
                              <button
                                key={dest.id}
                                onClick={() => updateForm("transformDestination", dest.id)}
                                className={`p-3 rounded-xl border-2 text-left transition-all ${
                                  form.transformDestination === dest.id
                                    ? "bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border-cyan-500 shadow-lg"
                                    : "bg-white/5 border-white/10 hover:border-white/20"
                                }`}
                                data-testid={`transform-dest-${dest.id}`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-xl">{dest.icon}</span>
                                  <div>
                                    <div className={`font-medium text-sm ${form.transformDestination === dest.id ? "text-cyan-400" : "text-white"}`}>
                                      {dest.label}
                                    </div>
                                    <div className="text-[10px] text-white/50">{dest.description}</div>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        {/* Duration for transformation */}
                        <div className="mt-4">
                          <Label className="text-white/70 text-xs mb-3 block">Video Duration</Label>
                          <div className="flex gap-3">
                            {DURATIONS.map((d) => (
                              <button
                                key={d.id}
                                onClick={() => updateForm("duration", d.id)}
                                className={`flex-1 p-3 rounded-xl border-2 text-center transition-all ${
                                  form.duration === d.id
                                    ? "bg-violet-500/20 border-violet-500 text-violet-400"
                                    : "bg-white/5 border-white/10 text-white/70 hover:border-white/20"
                                }`}
                              >
                                <div className="font-bold">{d.label}</div>
                                <div className="text-xs text-white/50">{d.description}</div>
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </motion.div>
                )}
                
                {/* Step 5: Generate */}
                {step === 5 && (
                  <motion.div
                    key="step5"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="space-y-4"
                  >
                    <div className="text-center mb-4">
                      <h2 className="text-xl font-bold text-white mb-2">Ready to Go Viral?</h2>
                      <p className="text-white/50 text-sm">
                        Create your {currentTemplate.label} video
                      </p>
                    </div>
                    
                    {/* Preview Summary */}
                    <div className="bg-gradient-to-br from-pink-500/10 to-purple-500/10 rounded-2xl p-5 border border-pink-500/20">
                      <div className="flex items-start gap-4">
                        {form.faceImagePreview ? (
                          <img
                            src={form.faceImagePreview}
                            alt="Your face"
                            className="w-20 h-20 rounded-xl object-cover border-2 border-pink-500/30"
                          />
                        ) : (
                          <div className="w-20 h-20 rounded-xl bg-white/10 flex items-center justify-center">
                            <Camera className="w-8 h-8 text-white/30" />
                          </div>
                        )}
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{currentTemplate.icon}</span>
                            <span className="text-white font-medium">{currentTemplate.label}</span>
                          </div>
                          <div className="text-xs text-white/60 space-y-0.5">
                            <p><span className="text-white/40">Duration:</span> {form.duration}s</p>
                            <p><span className="text-white/40">Music:</span> {MUSIC_MOODS.find(m => m.id === form.musicMood)?.label}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* AI Process */}
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <p className="text-white/70 text-sm font-medium mb-2">Enhanced 3-Step AI Process:</p>
                      <div className="grid grid-cols-1 gap-1.5">
                        <div className="flex items-center gap-2 text-white/60 text-xs">
                          <div className="w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center text-[10px] font-bold">1</div>
                          <span>AI analyzes your facial features</span>
                        </div>
                        <div className="flex items-center gap-2 text-white/60 text-xs">
                          <div className="w-5 h-5 rounded-full bg-pink-500/20 text-pink-400 flex items-center justify-center text-[10px] font-bold">2</div>
                          <span>AI creates your transformed {currentTemplate.label.toLowerCase()}</span>
                        </div>
                        <div className="flex items-center gap-2 text-white/60 text-xs">
                          <div className="w-5 h-5 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-[10px] font-bold">3</div>
                          <span>AI animates the transformation</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Generate Button */}
                    <Button
                      onClick={generateVideo}
                      disabled={isGenerating || !form.faceImage}
                      className="w-full h-14 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-400 hover:to-purple-400 text-white font-bold rounded-2xl shadow-lg shadow-pink-500/25 text-lg"
                      data-testid="generate-viral-btn"
                    >
                      {isGenerating ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Creating Magic...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-5 h-5 mr-2" />
                          Create My {currentTemplate.label}
                        </>
                      )}
                    </Button>
                    
                    <p className="text-center text-white/40 text-xs">
                      Enhanced 3-step AI process. Takes 3-5 minutes.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {/* Navigation Buttons */}
              <div className="flex justify-between mt-6 pt-4 border-t border-white/10">
                <Button
                  onClick={prevStep}
                  disabled={step === 1}
                  variant="ghost"
                  className="text-white/60 hover:text-white"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
                {step < 5 && (
                  <Button
                    onClick={nextStep}
                    disabled={!isStepComplete(step)}
                    className="bg-gradient-to-r from-pink-500/20 to-purple-500/20 hover:from-pink-500/30 hover:to-purple-500/30 text-white border border-pink-500/30"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          {/* Right: Preview / Result */}
          <div>
            {/* Generation Progress */}
            {isGenerating && (
              <div className="bg-gradient-to-br from-pink-500/10 to-purple-500/10 rounded-2xl p-6 border border-pink-500/20 mb-4">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">{status}</p>
                    <p className="text-white/50 text-sm">Creating your {currentTemplate.label.toLowerCase()}...</p>
                  </div>
                </div>
                <Progress value={progress} className="h-3 bg-white/10" />
                <p className="text-center text-pink-400 font-bold mt-2">{progress}%</p>
              </div>
            )}
            
            {/* Video Result */}
            {videoUrl && !isGenerating && (
              <div className="bg-gradient-to-br from-pink-500/10 to-purple-500/10 rounded-2xl overflow-hidden border border-pink-500/20">
                {/* Video Player */}
                <div className="relative aspect-[9/16] bg-black">
                  <video
                    ref={videoRef}
                    src={videoUrl}
                    className="w-full h-full object-contain"
                    loop
                    playsInline
                    onClick={togglePlay}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                  />
                  
                  {/* Play/Pause Overlay */}
                  <div 
                    className="absolute inset-0 flex items-center justify-center cursor-pointer"
                    onClick={togglePlay}
                  >
                    {!isPlaying && (
                      <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <Play className="w-8 h-8 text-white ml-1" />
                      </div>
                    )}
                  </div>
                  
                  {/* Controls */}
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                    <button
                      onClick={toggleMute}
                      className="p-2 rounded-full bg-black/50 text-white hover:bg-black/70"
                    >
                      {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="p-4 space-y-3">
                  <div className="flex items-center justify-center gap-2 text-pink-400">
                    <Check className="w-5 h-5" />
                    <span className="font-medium">Your viral video is ready!</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      onClick={downloadVideo}
                      className="bg-white/10 hover:bg-white/20 text-white"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                    <Button
                      onClick={() => navigate("/library")}
                      className="bg-gradient-to-r from-pink-500 to-purple-500 text-white"
                    >
                      <Video className="w-4 h-4 mr-2" />
                      View in Library
                    </Button>
                  </div>
                  
                  {/* Create Another */}
                  <Button
                    onClick={() => {
                      setVideoUrl(null);
                      setVideoId(null);
                      setStep(1);
                      updateForm("faceImage", null);
                      updateForm("faceImagePreview", null);
                    }}
                    variant="ghost"
                    className="w-full text-white/60 hover:text-white"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Create Another
                  </Button>
                </div>
              </div>
            )}
            
            {/* Info Card when not generating and no video */}
            {!isGenerating && !videoUrl && (
              <div className="bg-gradient-to-br from-pink-500/5 to-purple-500/5 rounded-2xl p-6 border border-pink-500/10">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-pink-500/20 to-purple-500/20 flex items-center justify-center mb-4">
                    <span className="text-4xl">{currentTemplate.icon}</span>
                  </div>
                  <h3 className="text-white font-bold text-lg mb-2">{currentTemplate.label}</h3>
                  <p className="text-white/50 text-sm">
                    {currentTemplate.description}. Upload your face and watch AI create the transformation!
                  </p>
                </div>
                
                {/* How it works */}
                <div className="space-y-3">
                  <p className="text-white/70 text-sm font-medium">How it works:</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 text-white/60 text-sm">
                      <div className="w-6 h-6 rounded-full bg-pink-500/20 text-pink-400 flex items-center justify-center text-xs font-bold">1</div>
                      <span>Choose your viral trend</span>
                    </div>
                    <div className="flex items-center gap-3 text-white/60 text-sm">
                      <div className="w-6 h-6 rounded-full bg-pink-500/20 text-pink-400 flex items-center justify-center text-xs font-bold">2</div>
                      <span>Upload your face photo</span>
                    </div>
                    <div className="flex items-center gap-3 text-white/60 text-sm">
                      <div className="w-6 h-6 rounded-full bg-pink-500/20 text-pink-400 flex items-center justify-center text-xs font-bold">3</div>
                      <span>AI creates your transformation</span>
                    </div>
                    <div className="flex items-center gap-3 text-white/60 text-sm">
                      <div className="w-6 h-6 rounded-full bg-pink-500/20 text-pink-400 flex items-center justify-center text-xs font-bold">4</div>
                      <span>Share and watch it go viral!</span>
                    </div>
                  </div>
                </div>
                
                {/* Available Templates */}
                <div className="mt-6">
                  <p className="text-white/50 text-xs mb-3">Available Trends:</p>
                  <div className="flex flex-wrap gap-2">
                    {VIRAL_TEMPLATES.map((t) => (
                      <span 
                        key={t.id}
                        className={`text-xs px-2 py-1 rounded-full ${
                          t.id === form.template 
                            ? "bg-pink-500/20 text-pink-400 border border-pink-500/30"
                            : "bg-white/5 text-white/40"
                        }`}
                      >
                        {t.icon} {t.label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Upgrade Modal */}
      <UpgradeModal
        open={upgradeModalOpen}
        onOpenChange={setUpgradeModalOpen}
        errorType={upgradeErrorType}
        errorData={upgradeErrorData}
      />
    </div>
  );
}
